import api from '../axios/axiosConfig';
import { LocationContextType } from '../context/LocationContext';

type ChargerSite = {
  csId: string;
  csLocation: {
    type: string;
    coordinates: [number, number]; // [lng, lat]
  };
  [key: string]: any;
};

type ChargerSiteWithDistance = ChargerSite & {
  userDistance: number;
};

/**
 * Busca sites de carregamento próximos
 */
export const getChargerSites = async (
  lat: number,
  lng: number,
  locationContext: LocationContextType,
  dist: number = 10000
): Promise<ChargerSiteWithDistance[]> => {
  const radius = Math.max(dist, 10000);

  try {
    const response = await api.get<ChargerSite[]>(`/ChargerSitesSearch`, {
      params: {
        lat,
        lng,
        dist: radius,
        free: '',
        plug: '',
        speed: '',
      },
    });

    return orderSitesByDistance(response.data, locationContext);
  } catch (error: any) {
    // Só loga se não for erro de rede ou página oculta
    if (error?.message !== 'Network Error' && error?.message !== 'Page not visible') {
      console.error('Erro ao buscar sites de carregamento:', error);
    }
    throw error;
  }
};

export type ChargerSiteStatus = {
  available: number;
  inUse: number;
  offLine: number;
  total: number;
  onHold: number;
};

/**
 * Busca só o status dos conectores dos sites informados (refresh dos pins do mapa)
 */
export const getChargerSitesStatus = async (
  siteIds: string[]
): Promise<Record<string, ChargerSiteStatus>> => {
  const response = await api.post<Record<string, ChargerSiteStatus>>(
    `/ChargerSitesStatus`,
    { siteIds }
  );
  return response.data ?? {};
};

/**
 * Ordena os sites por distância do usuário
 */
export const orderSitesByDistance = (
  sites: ChargerSite[],
  locationContext: LocationContextType
): ChargerSiteWithDistance[] => {
  return sites
    .map((site) => {
      const siteLocation = {
        lat: site.csLocation.coordinates[1],
        lng: site.csLocation.coordinates[0],
      };

      const distance = locationContext.getDistance(
        { from: siteLocation, to: locationContext.userLocation },
        'km',
        1
      );

      return { ...site, userDistance: distance };
    })
    .sort((a, b) => a.userDistance - b.userDistance);
};

/**
 * Busca site de carregamento por ID
 */
export const getChargerSiteById = async (
  csId: string
): Promise<ChargerSite> => {
  try {
    const response = await api.get<ChargerSite>(`/ChargerSites/${csId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar site de carregamento por ID:', error);
    throw error;
  }
};

const FILE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hora
const FILE_CACHE_PREFIX = 'file_cache:';

type FileCacheEntry = {
  data: any;
  expiresAt: number;
};

const fileMemoryCache = new Map<string, FileCacheEntry>();

const getCachedFile = (id: string): any | null => {
  const memEntry = fileMemoryCache.get(id);
  if (memEntry) {
    if (Date.now() < memEntry.expiresAt) return memEntry.data;
    fileMemoryCache.delete(id);
  }

  try {
    const raw = sessionStorage.getItem(FILE_CACHE_PREFIX + id);
    if (raw) {
      const entry: FileCacheEntry = JSON.parse(raw);
      if (Date.now() < entry.expiresAt) {
        fileMemoryCache.set(id, entry);
        return entry.data;
      }
      sessionStorage.removeItem(FILE_CACHE_PREFIX + id);
    }
  } catch {
    // sessionStorage indisponível ou JSON inválido
  }

  return null;
};

const setCachedFile = (id: string, data: any): void => {
  const entry: FileCacheEntry = { data, expiresAt: Date.now() + FILE_CACHE_TTL_MS };
  fileMemoryCache.set(id, entry);
  try {
    sessionStorage.setItem(FILE_CACHE_PREFIX + id, JSON.stringify(entry));
  } catch {
    // quota excedida ou sessionStorage indisponível
  }
};

/**
 * Busca arquivo por ID (com cache em memória + sessionStorage, TTL de 1h)
 */
export const getFile = async (id: string): Promise<any> => {
  const cached = getCachedFile(id);
  if (cached !== null) return cached;

  try {
    const response = await api.get(`/Files/${id}`);
    setCachedFile(id, response.data);
    return response.data;
  } catch (error: any) {
    // Só loga se não for erro de rede ou página oculta
    if (error?.message !== 'Network Error' && error?.message !== 'Page not visible') {
      console.error('Erro ao buscar arquivo:', error);
    }
    throw error;
  }
};
